import { ArrowDown, ArrowUp, Check, Pencil, Trash2 } from "lucide-react";
import { resumoDaFace, totalDoItem, type MarcacaoFacial } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { DENTIST_STATUS_META, dentistStatusBucket } from "@/components/prosthesis/dentist-status";
import { corDoItem } from "./cores-do-plano";
import { reais } from "./use-plano";

export type ItemDoPlanoTela = {
  id: string;
  nome: string;
  dentes: string | null;
  precoCents: number | null;
  porDente: boolean;
  quantidade: number;
  offeredService: { id: string; priceCents: number; color: string | null } | null;
  unidade: string | null;
  marcacoes: MarcacaoFacial[];
  feitoEm: Date | string | null;
  pedido: { id: string; numero: number | null; status: string; labName: string | null } | null;
};

const icone = "inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30 dark:hover:bg-muted";

/**
 * Itens do plano. Passar o mouse (ou tocar) num item destaca os dentes dele na arcada. Antes da
 * aprovação: editar, remover e reordenar; aprovado: marcar feito e pedir prótese.
 */
export function ItensDoPlano({
  itens,
  editandoId,
  destacadoId,
  editavel,
  aprovado,
  podeMontar,
  podePedirProtese,
  onDestacar,
  onFixarDestaque,
  onEditar,
  onRemover,
  onMover,
  onMarcarFeito,
  onPedirProtese,
  onAbrirPedido,
}: {
  itens: ItemDoPlanoTela[];
  editandoId: string | null;
  destacadoId: string | null;
  editavel: boolean;
  aprovado: boolean;
  podeMontar: boolean;
  podePedirProtese: boolean;
  onDestacar: (id: string | null) => void;
  onFixarDestaque: (id: string) => void;
  onEditar: (item: ItemDoPlanoTela) => void;
  onRemover: (item: ItemDoPlanoTela) => void;
  onMover: (item: ItemDoPlanoTela, direcao: -1 | 1) => void;
  onMarcarFeito: (item: ItemDoPlanoTela, feito: boolean) => void;
  onPedirProtese: (item: ItemDoPlanoTela) => void;
  onAbrirPedido: (pedidoId: string) => void;
}) {
  if (itens.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
        Nenhum procedimento ainda. Marque os dentes na arcada e escolha o procedimento.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border dark:border-border" onMouseLeave={() => onDestacar(null)}>
      {itens.map((i, pos) => {
        const feito = !!i.feitoEm;
        const total = totalDoItem(i);
        return (
          <li
            key={i.id}
            onMouseEnter={() => onDestacar(i.id)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 transition-colors",
              destacadoId === i.id && "bg-zinc-50 dark:bg-muted/40",
              editandoId === i.id && "bg-brand-50/60 dark:bg-primary/10",
            )}
          >
            {aprovado && podeMontar ? (
              <button
                type="button"
                aria-label={feito ? "Desmarcar como feito" : "Marcar como feito"}
                onClick={() => onMarcarFeito(i, !feito)}
                className={cn(
                  "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border",
                  feito ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-300 hover:border-emerald-500",
                )}
              >
                {feito && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </button>
            ) : feito ? (
              <Check className="h-4 w-4 shrink-0 text-emerald-600" />
            ) : null}
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: corDoItem({ ...i, feitoEm: null }) }} />
            <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onFixarDestaque(i.id)}>
              <p className={cn("text-sm font-medium text-zinc-900 dark:text-foreground", feito && "text-zinc-500 line-through")}>{i.nome}</p>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-zinc-500 dark:text-muted-foreground">
                <span>{[i.dentes, resumoDaFace(i.marcacoes, i.unidade)].filter(Boolean).join(" · ") || "Sem dente definido"}</span>
                {i.pedido ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAbrirPedido(i.pedido!.id);
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-fuchsia-50 px-2 py-0.5 text-[11.5px] font-medium text-fuchsia-700 hover:bg-fuchsia-100"
                  >
                    <CoroaDentariaIcon className="h-3 w-3" />
                    Pedido {i.pedido.numero ? `nº ${i.pedido.numero}` : ""} · {DENTIST_STATUS_META[dentistStatusBucket(i.pedido.status as never)].label}
                  </button>
                ) : (
                  podePedirProtese && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPedirProtese(i);
                      }}
                      className="font-semibold text-brand-700 hover:underline"
                    >
                      Pedir prótese
                    </button>
                  )
                )}
              </div>
            </div>
            <span className={cn("shrink-0 text-right tabular-nums", total == null ? "text-sm text-amber-600" : "text-sm text-zinc-900 dark:text-foreground")}>
              {reais(total)}
              {i.porDente && i.quantidade > 1 && i.precoCents != null && (
                <span className="block text-[11px] text-zinc-400">
                  {i.quantidade} × {reais(i.precoCents)}
                </span>
              )}
            </span>
            {editavel && (
              <span className="flex">
                <button type="button" aria-label="Subir" disabled={pos === 0} className={icone} onClick={() => onMover(i, -1)}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" aria-label="Descer" disabled={pos === itens.length - 1} className={icone} onClick={() => onMover(i, 1)}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button type="button" aria-label="Editar" className={icone} onClick={() => onEditar(i)}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" aria-label="Remover" className={cn(icone, "hover:text-red-600")} onClick={() => onRemover(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
